/**
 * 등온 압축성 유동 — 플레어 헤더 배압 계산의 뼈대.
 *
 * 근거: API Standard 521, 2020년 6월판 5.5.5 "Isothermal Pressure Drop
 *       Calculation Method"
 *
 * ⚠ **여기서는 기존 `pipeFlow.ts` 를 쓰지 않는다.**
 *   그쪽은 밀도가 일정하다고 보는 비압축성 계산이다. 플레어 헤더는
 *   팁까지 가는 동안 압력이 크게 떨어져 밀도가 몇 배로 변하므로
 *   비압축성 식으로는 손실을 크게 과소평가한다(5.5.5: "flow should be
 *   rated as compressible").
 *
 * 표준이 정한 방법:
 *   · 등온식과 단열식 중 **등온식을 권장**한다. 실제 유동은 그 사이에
 *     있고 등온 쪽이 약간 보수적이다(5.5.5).
 *   · **압력을 아는 계통 출구(플레어 팁)에서 시작해 거꾸로 거슬러**
 *     올라가며 각 PRD 의 배압을 확인한다.
 *   · 계산은 **관경이 일정한 구간마다** 단계적으로 한다.
 *
 * 식:
 *   (32)  f·l/d = (1/Ma₁²)·[1 − (p₂/p₁)²] − ln(p₁/p₂)²
 *   (33)  f·l/d = (1/Ma₂²)·(p₁/p₂)²·[1 − (p₂/p₁)²] − ln(p₁/p₂)²
 *   (34)  Ma₂ = 3.23×10⁻⁵ · (qm/(p₂·d²)) · (Z·T/M)^0.5      [SI]
 *   (36)  p_crit = 3.23×10⁻⁵ · (qm/d²) · (Z·T/M)^0.5        [SI]
 *
 * 단위: qm [kg/h] · p [kPa 절대] · d [m] · l [m] · T [K]
 */

/**
 * 식 (34)·(36) 의 SI 상수.
 *
 * ⚠ 표준값은 **유효숫자 3자리로 반올림된 값**이다. 차원 유도로 얻는
 *   정확한 값은 (4/(π·3600·1000))·√8314.46 = 3.2250×10⁻⁵ 이다.
 *
 *   차이 0.155 % 는 마하수에 그대로, 압력강하에는 제곱으로 실려
 *   **0.31 % 크게** 나온다. 방향이 보수적이고 표준값을 그대로 쓰는 것이
 *   원칙이므로 고치지 않는다. 시험이 이 관계를 고정하고 있으니,
 *   나중에 "0.3 % 가 안 맞는다"고 상수를 바꾸지 말 것.
 */
export const ISOTHERMAL_SI_CONSTANT = 3.23e-5;

/**
 * 일반기체상수 R [J/(kmol·K)] — 위 상수를 유도할 때 쓴 값과 같다.
 *
 * 밀도 ρ = pM/(ZRT) 를 구할 때도 이 값을 써야 마하수와 앞뒤가 맞는다.
 * (M 을 g/mol 로 넣으면 kmol 기준과 수치가 같다.)
 */
export const UNIVERSAL_GAS_CONSTANT = 8314.46;

/**
 * 식 (34) — 구간 출구의 등온 마하수.
 *
 * ⚠ 이 마하수는 **등온 음속 √(ZRT/M) 기준**이다. 단열 음속 √(kZRT/M)
 *   기준의 마하수와 다르다(√k 배만큼). 식에 k 가 없는 이유가 이것이다.
 *   등온 유동의 초크 한계가 이 값 1.0 이다.
 */
export function isothermalMachAtOutlet(
  massFlowKgH: number,
  outletPressureKpaAbs: number,
  innerDiameterM: number,
  compressibility: number,
  temperatureK: number,
  molecularWeight: number,
): number {
  return (
    ISOTHERMAL_SI_CONSTANT *
    (massFlowKgH / (outletPressureKpaAbs * innerDiameterM * innerDiameterM)) *
    Math.sqrt((compressibility * temperatureK) / molecularWeight)
  );
}

/**
 * 식 (36) — 임계(초크) 압력.
 *
 * 식 (34)에 Ma₂ = 1.0 을 넣은 것이다. 구간 출구 압력이 이 값보다 낮으면
 * 그 관경으로는 유량을 흘릴 수 없다.
 *
 * 관계: Ma₂ = p_crit / p₂ — 임계압력을 알면 마하수는 나눗셈 한 번이다.
 */
export function criticalPressureKpaAbs(
  massFlowKgH: number,
  innerDiameterM: number,
  compressibility: number,
  temperatureK: number,
  molecularWeight: number,
): number {
  return (
    ISOTHERMAL_SI_CONSTANT *
    (massFlowKgH / (innerDiameterM * innerDiameterM)) *
    Math.sqrt((compressibility * temperatureK) / molecularWeight)
  );
}

/**
 * 식 (33)의 좌변에 대응하는 함수.
 *
 * r = p₁/p₂ 로 두고 정리하면 식 (32)와 식 (33)이 같은 꼴이 된다:
 *
 *     f·l/d = (r² − 1)/Ma₂² − 2·ln r
 *
 * r ≥ 1 에서 단조증가하므로 해가 하나뿐이다.
 * (도함수 = 2(r² − Ma₂²)/(r·Ma₂²) > 0, 아음속이면 r > 1 > Ma₂)
 */
export function isothermalResistance(
  pressureRatio: number,
  outletMach: number,
): number {
  const r = pressureRatio;
  return (r * r - 1) / (outletMach * outletMach) - 2 * Math.log(r);
}

export interface IsothermalSegmentInput {
  /** qm — 이 구간을 흐르는 질량유량 (kg/h) */
  readonly massFlowKgH: number;
  /** p₂ — 구간 출구(하류) 절대압 (kPa) */
  readonly outletPressureKpaAbs: number;
  /** d — 내경 (m) */
  readonly innerDiameterM: number;
  /**
   * l — 등가길이 (m). **직관 길이 + 부속 등가길이**를 넣는다.
   *
   * ⚠ 대기 방출이라도 **출구 손실 K=1 을 넣지 않는다.** 등온식 유도에
   *   이미 들어 있다(5.5.9).
   */
  readonly equivalentLengthM: number;
  /** f — Moody 마찰계수 */
  readonly frictionFactor: number;
  readonly compressibility: number;
  readonly temperatureK: number;
  readonly molecularWeight: number;
}

export interface IsothermalSegmentSuccess {
  readonly ok: true;
  /** p₁ — 구간 입구(상류) 절대압 (kPa) */
  readonly inletPressureKpaAbs: number;
  /** p₂ — 구간 출구(하류) 절대압 (kPa) */
  readonly outletPressureKpaAbs: number;
  /** 압력강하 (kPa) */
  readonly pressureDropKpa: number;
  /** p₁/p₂ */
  readonly pressureRatio: number;
  /** Ma₂ — 출구 등온 마하수 */
  readonly outletMach: number;
  /** Ma₁ — 입구 등온 마하수 (= Ma₂/r) */
  readonly inletMach: number;
  /** 임계압력 (kPa 절대) */
  readonly criticalPressureKpaAbs: number;
  /** f·l/d */
  readonly resistance: number;
  readonly equation: "API 521 (33)";
}

export interface IsothermalSegmentFailure {
  readonly ok: false;
  readonly reason: "invalid-input" | "choked" | "no-solution";
  readonly message: string;
}

export type IsothermalSegmentResult =
  | IsothermalSegmentSuccess
  | IsothermalSegmentFailure;

/** r 탐색 상한. 이보다 커지면 관경이 터무니없이 작다는 뜻이다. */
const MAX_PRESSURE_RATIO = 1e6;

/**
 * 구간 하나의 입구 압력을 구한다 — 표준이 말하는 "거꾸로 거슬러 올라가기".
 *
 * 출구 압력 p₂ 를 알 때 입구 압력 p₁ 을 준다. 식 (33)을 r 에 대해
 * 이분법으로 푼다(단조증가라 해가 하나뿐이다).
 */
export function solveInletPressure(
  input: IsothermalSegmentInput,
): IsothermalSegmentResult {
  const {
    massFlowKgH: qm,
    outletPressureKpaAbs: p2,
    innerDiameterM: d,
    equivalentLengthM: l,
    frictionFactor: f,
    compressibility: Z,
    temperatureK: T,
    molecularWeight: M,
  } = input;

  const positives: ReadonlyArray<readonly [string, number]> = [
    ["질량유량", qm],
    ["출구 압력", p2],
    ["내경", d],
    ["마찰계수", f],
    ["압축계수", Z],
    ["온도", T],
    ["분자량", M],
  ];
  for (const [label, v] of positives) {
    if (!Number.isFinite(v) || v <= 0) {
      return {
        ok: false,
        reason: "invalid-input",
        message: `${label} 은(는) 0보다 큰 유한한 값이어야 합니다.`,
      };
    }
  }
  if (!Number.isFinite(l) || l < 0) {
    return {
      ok: false,
      reason: "invalid-input",
      message: "등가길이는 0 이상이어야 합니다.",
    };
  }

  const pCrit = criticalPressureKpaAbs(qm, d, Z, T, M);
  const ma2 = pCrit / p2; // 식 (34) 와 같다

  if (ma2 >= 1) {
    return {
      ok: false,
      reason: "choked",
      message:
        `이 구간은 초크(임계) 상태입니다 — 출구 압력 ${p2.toFixed(1)} kPa abs 가 ` +
        `임계압력 ${pCrit.toFixed(1)} kPa abs 이하입니다(Ma₂ = ${ma2.toFixed(3)}). ` +
        "관경을 키우거나 유량을 줄여야 합니다(API 521 식 36).",
    };
  }

  const target = (f * l) / d;

  // 길이가 0 이면 압력강하도 0 이다.
  if (target === 0) {
    return {
      ok: true,
      inletPressureKpaAbs: p2,
      outletPressureKpaAbs: p2,
      pressureDropKpa: 0,
      pressureRatio: 1,
      outletMach: ma2,
      inletMach: ma2,
      criticalPressureKpaAbs: pCrit,
      resistance: 0,
      equation: "API 521 (33)",
    };
  }

  // g(r) = 저항(r) − target 은 r ≥ 1 에서 단조증가하고 g(1) = −target < 0.
  let lo = 1;
  let hi = 2;
  while (isothermalResistance(hi, ma2) < target) {
    hi *= 2;
    if (hi > MAX_PRESSURE_RATIO) {
      return {
        ok: false,
        reason: "no-solution",
        message:
          "압력비가 지나치게 커집니다. 관경이 너무 작거나 길이가 너무 깁니다.",
      };
    }
  }

  for (let i = 0; i < 200; i += 1) {
    const mid = (lo + hi) / 2;
    if (isothermalResistance(mid, ma2) < target) lo = mid;
    else hi = mid;
    if (hi - lo < 1e-13 * hi) break;
  }
  const r = (lo + hi) / 2;

  return {
    ok: true,
    inletPressureKpaAbs: p2 * r,
    outletPressureKpaAbs: p2,
    pressureDropKpa: p2 * (r - 1),
    pressureRatio: r,
    outletMach: ma2,
    inletMach: ma2 / r,
    criticalPressureKpaAbs: pCrit,
    resistance: target,
    equation: "API 521 (33)",
  };
}

export interface IsothermalOutletInput {
  /** qm — 이 구간을 흐르는 질량유량 (kg/h) */
  readonly massFlowKgH: number;
  /** p₁ — 구간 입구(상류) 절대압 (kPa) */
  readonly inletPressureKpaAbs: number;
  readonly innerDiameterM: number;
  readonly equivalentLengthM: number;
  readonly frictionFactor: number;
  readonly compressibility: number;
  readonly temperatureK: number;
  readonly molecularWeight: number;
}

/**
 * 구간 하나의 출구 압력을 구한다 — `solveInletPressure()` 의 반대 방향.
 *
 * 표준(5.5.5)은 "출구를 알 때 입구를 거슬러 올라가는" 방향만 다룬다.
 * 공정 흐름도처럼 **상류 압력을 알고 하류를 앞으로 계산**해야 할 때는
 * 같은 식 (33)을 출구 압력에 대해 풀어야 한다.
 *
 * p₁ 이 고정이면 r=p₁/p₂ 와 Ma₂=p_crit/p₂ 가 **둘 다 p₂ 에 따라 같이
 * 바뀌므로**, `solveInletPressure()` 처럼 r 하나만 놓고 이분법을 돌릴 수
 * 없다. 식 (33)에 두 관계를 대입해 p₂ 하나로 정리하면:
 *
 *   h(p₂) = (p₁² − p₂²)/p_crit² + 2·ln(p₂/p₁) − f·l/d = 0
 *
 * h 는 p₂ ∈ (0, p₁] 에서 p₂ = p_crit 일 때 유일한 극댓값을 갖는 오목함수다
 * (h′ = 2/p₂ − 2p₂/p_crit², h″ < 0 항상). 따라서:
 *   · h(p_crit) < 0 이면 아음속 해가 없다 — 이미 초크 상태.
 *   · h(p_crit) ≥ 0 이면 (p_crit, p₁] 구간에서 h 가 단조감소하므로
 *     그 구간에서 이분법으로 유일해를 구한다(물리적으로 맞는 아음속 쪽 근).
 */
export function solveOutletPressure(
  input: IsothermalOutletInput,
): IsothermalSegmentResult {
  const {
    massFlowKgH: qm,
    inletPressureKpaAbs: p1,
    innerDiameterM: d,
    equivalentLengthM: l,
    frictionFactor: f,
    compressibility: Z,
    temperatureK: T,
    molecularWeight: M,
  } = input;

  const positives: ReadonlyArray<readonly [string, number]> = [
    ["질량유량", qm],
    ["입구 압력", p1],
    ["내경", d],
    ["마찰계수", f],
    ["압축계수", Z],
    ["온도", T],
    ["분자량", M],
  ];
  for (const [label, v] of positives) {
    if (!Number.isFinite(v) || v <= 0) {
      return {
        ok: false,
        reason: "invalid-input",
        message: `${label} 은(는) 0보다 큰 유한한 값이어야 합니다.`,
      };
    }
  }
  if (!Number.isFinite(l) || l < 0) {
    return {
      ok: false,
      reason: "invalid-input",
      message: "등가길이는 0 이상이어야 합니다.",
    };
  }

  const pCrit = criticalPressureKpaAbs(qm, d, Z, T, M);
  const target = (f * l) / d;

  const h = (p2: number) =>
    (p1 * p1 - p2 * p2) / (pCrit * pCrit) + 2 * Math.log(p2 / p1) - target;

  if (target === 0) {
    return {
      ok: true,
      inletPressureKpaAbs: p1,
      outletPressureKpaAbs: p1,
      pressureDropKpa: 0,
      pressureRatio: 1,
      outletMach: pCrit / p1,
      inletMach: pCrit / p1,
      criticalPressureKpaAbs: pCrit,
      resistance: 0,
      equation: "API 521 (33)",
    };
  }

  if (pCrit >= p1) {
    return {
      ok: false,
      reason: "choked",
      message:
        `이 구간은 임계압력(${pCrit.toFixed(1)} kPa abs)이 입구 압력 ` +
        `${p1.toFixed(1)} kPa abs 이상입니다 — 관경을 키우거나 유량을 ` +
        "줄여야 합니다(API 521 식 36).",
    };
  }

  const hAtCrit = h(pCrit);
  if (hAtCrit < 0) {
    return {
      ok: false,
      reason: "choked",
      message:
        `이 구간은 초크(임계) 상태입니다 — 입구 압력 ${p1.toFixed(1)} kPa abs 로는 ` +
        `이 유량·등가길이를 아음속으로 흘릴 수 없습니다(임계압력 ` +
        `${pCrit.toFixed(1)} kPa abs). 관경을 키우거나 등가길이를 줄이십시오.`,
    };
  }

  // (p_crit, p1] 에서 h 는 단조감소한다: h(p_crit) = hAtCrit ≥ 0, h(p1) = −target < 0.
  let lo = pCrit;
  let hi = p1;
  for (let i = 0; i < 200; i += 1) {
    const mid = (lo + hi) / 2;
    if (h(mid) > 0) lo = mid;
    else hi = mid;
    if (hi - lo < 1e-13 * p1) break;
  }
  const p2 = (lo + hi) / 2;
  const r = p1 / p2;
  const ma2 = pCrit / p2;

  return {
    ok: true,
    inletPressureKpaAbs: p1,
    outletPressureKpaAbs: p2,
    pressureDropKpa: p1 - p2,
    pressureRatio: r,
    outletMach: ma2,
    inletMach: ma2 / r,
    criticalPressureKpaAbs: pCrit,
    resistance: target,
    equation: "API 521 (33)",
  };
}

/* ── 혼합 물성 ────────────────────────────────────────────
 * 여러 밸브의 배출가스가 헤더에서 섞인다. 구간마다 물성이 달라지므로
 * 합류할 때마다 다시 계산해야 한다.
 */

export interface StreamProperties {
  readonly massFlowKgH: number;
  readonly molecularWeight: number;
  readonly temperatureK: number;
  readonly compressibility: number;
  /** 절대점도 (cP). 마찰계수를 Re 로 구하는 데 쓴다 (KOSHA D-63 5.2.2 (라)). */
  readonly viscosityCp: number;
}

export interface MixedProperties {
  readonly massFlowKgH: number;
  readonly molecularWeight: number;
  readonly temperatureK: number;
  readonly compressibility: number;
  /** 절대점도 (cP) */
  readonly viscosityCp: number;
  readonly warnings: readonly string[];
}

/**
 * 배출가스 여러 갈래를 섞는다.
 *
 *   분자량 : M = Σwᵢ / Σ(wᵢ/Mᵢ)        — KOSHA D-63 식 (5-2). 몰수 보존, **정확**
 *   온도   : T = Σ(wᵢTᵢ) / Σwᵢ         — KOSHA D-63 식 (5-3). 질량가중
 *   점도   : μ = Σ(xᵢμᵢ√Mᵢ) / Σ(xᵢ√Mᵢ) — KOSHA D-63 식 (5-4). 몰분율·√M 가중
 *   압축계수: Z = Σ(nᵢZᵢ) / Σnᵢ         — 몰분율 가중 (KOSHA 는 Z=1 로 둔다)
 *
 * ⚠ 온도와 압축계수는 **근사다.** 엄밀하게는 엔탈피 수지를 풀어야 하고,
 *   비열이 크게 다른 갈래가 섞이면 어긋난다. 온도 차가 큰 조합에서는
 *   경고를 띄운다.
 */
export function mixStreams(
  streams: readonly StreamProperties[],
): MixedProperties | null {
  const valid = streams.filter((s) => s.massFlowKgH > 0);
  if (valid.length === 0) return null;

  let totalMass = 0;
  let totalMoles = 0;
  let massTempSum = 0;
  let moleZSum = 0;
  // 점도는 몰분율 x √분자량 으로 가중한다 (Herning–Zipperer, KOSHA 식 5-4).
  // 몰분율은 nᵢ/Σn 이므로 분모가 약분되어 몰수로 계산해도 같다.
  let viscNum = 0;
  let viscDen = 0;

  for (const s of valid) {
    if (
      !Number.isFinite(s.molecularWeight) ||
      s.molecularWeight <= 0 ||
      !Number.isFinite(s.temperatureK) ||
      s.temperatureK <= 0 ||
      !Number.isFinite(s.compressibility) ||
      s.compressibility <= 0 ||
      !Number.isFinite(s.viscosityCp) ||
      s.viscosityCp <= 0
    ) {
      return null;
    }
    const moles = s.massFlowKgH / s.molecularWeight;
    const weight = moles * Math.sqrt(s.molecularWeight);
    totalMass += s.massFlowKgH;
    totalMoles += moles;
    massTempSum += s.massFlowKgH * s.temperatureK;
    moleZSum += moles * s.compressibility;
    viscNum += weight * s.viscosityCp;
    viscDen += weight;
  }

  const warnings: string[] = [];
  if (valid.length > 1) {
    const temps = valid.map((s) => s.temperatureK);
    const spread = Math.max(...temps) - Math.min(...temps);
    if (spread > 100) {
      warnings.push(
        `합류하는 갈래의 온도 차가 ${spread.toFixed(0)} K 로 큽니다. ` +
          "혼합 온도를 질량가중 평균으로 잡았으나, 비열이 크게 다르면 " +
          "엔탈피 수지로 다시 확인하십시오.",
      );
    }
  }

  return {
    massFlowKgH: totalMass,
    molecularWeight: totalMass / totalMoles,
    temperatureK: massTempSum / totalMass,
    compressibility: moleZSum / totalMoles,
    viscosityCp: viscNum / viscDen,
    warnings,
  };
}
